/** Ciclo de vida de la OS: CreateOrder inicia en CONFIRMADO; EN PROCESO lo asigna Financials. */
export enum OrderStatus {
  PRELIMINAR = 'PRELIMINAR',
  CONFIRMADO = 'CONFIRMADO',
  EN_PROCESO = 'EN PROCESO',
  PROCESADO = 'PROCESADO',
  ELIMINADO = 'ELIMINADO'
}

export enum OrderReservationStatus {
  EN_ESPERA = 'EN ESPERA',
  CONFIRMADO = 'CONFIRMADO',
  RECHAZADO = 'RECHAZADO',
  ELIMINADO = 'ELIMINADO'
}

/** Estados técnicos del PDF; no confundir con OrderStatus. */
export enum OrderPdfStatus {
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  GENERATED = 'GENERATED',
  FAILED = 'FAILED'
}

export function hasActiveReservationStatus(
  status: string | null | undefined
): boolean {
  return (
    status === OrderReservationStatus.EN_ESPERA ||
    status === OrderReservationStatus.CONFIRMADO ||
    status === OrderReservationStatus.RECHAZADO
  );
}

export function formatOrderDisplayCode(
  orderNumber?: string | number | null,
  orderStatus?: string | null
): string {
  const number = String(orderNumber ?? '').trim();
  if (!number) {
    return '';
  }
  return orderStatus === OrderStatus.PRELIMINAR ? `P-${number}` : number;
}

export function canShowOrderPdf(row: {
  order?: {
    orderId?: string | null;
    orderNumber?: string | null;
    reservationStatus?: string | null;
    orderStatus?: string | null;
    pdfStatus?: string | null;
  };
}): boolean {
  if (row.order?.orderStatus === OrderStatus.PRELIMINAR) {
    return false;
  }
  if (row.order?.pdfStatus === OrderPdfStatus.NOT_APPLICABLE) {
    return false;
  }
  return (
    Boolean(row.order?.orderId) ||
    Boolean(row.order?.orderNumber) ||
    hasActiveReservationStatus(row.order?.reservationStatus)
  );
}

export function normalizePdfStatus(
  pdfStatus: string | null | undefined
): OrderPdfStatus | null {
  const normalized = pdfStatus?.trim().toUpperCase();
  if (!normalized) {
    return null;
  }

  if (normalized === OrderPdfStatus.NOT_APPLICABLE) {
    return null;
  }

  if (
    Object.values(OrderPdfStatus).includes(normalized as OrderPdfStatus)
  ) {
    return normalized as OrderPdfStatus;
  }

  return null;
}

export function resolveOrderPdfStatus(
  order?: {
    pdfStatus?: string | null;
    orderNumber?: string | null;
    orderId?: string | null;
  } | null
): OrderPdfStatus | null {
  const normalized = normalizePdfStatus(order?.pdfStatus ?? null);
  if (normalized) {
    return normalized;
  }

  if (order?.orderId || order?.orderNumber) {
    return OrderPdfStatus.PENDING;
  }

  return null;
}

export function isPdfGenerating(
  pdfStatus: string | null | undefined
): boolean {
  const status = resolveOrderPdfStatus({ pdfStatus });
  return (
    status === OrderPdfStatus.PENDING ||
    status === OrderPdfStatus.PROCESSING
  );
}

export function isPdfReady(pdfStatus: string | null | undefined): boolean {
  return resolveOrderPdfStatus({ pdfStatus }) === OrderPdfStatus.GENERATED;
}

export function isPdfFailed(pdfStatus: string | null | undefined): boolean {
  return resolveOrderPdfStatus({ pdfStatus }) === OrderPdfStatus.FAILED;
}

export function canOpenOrderPdf(row: {
  order?: { pdfStatus?: string | null };
}): boolean {
  return isPdfReady(row.order?.pdfStatus);
}

export function isOrderStatusConfirmed(
  orderStatus: string | null | undefined
): boolean {
  return orderStatus?.trim().toUpperCase() === OrderStatus.CONFIRMADO;
}

export function isOrderEditable(row: {
  order?: { orderId?: string | null; orderStatus?: string | null };
}): boolean {
  const status = row.order?.orderStatus;
  return (
    Boolean(row.order?.orderId) &&
    (isOrderStatusConfirmed(status) ||
      status === OrderStatus.PRELIMINAR)
  );
}

export function canDeleteServiceOrder(row: {
  order?: {
    orderId?: string | null;
    orderNumber?: string | null;
    reservationStatus?: string | null;
    orderStatus?: string | null;
  };
}): boolean {
  return (
    Boolean(row.order?.orderId) &&
    isOrderStatusDeletable(row.order?.orderStatus)
  );
}

export function canShowCreditNote(row: {
  order?: { orderStatus?: string | null };
}): boolean {
  const status = row.order?.orderStatus;
  return (
    status === OrderStatus.EN_PROCESO || status === OrderStatus.PROCESADO
  );
}

export function isOrderStatusDeletable(
  orderStatus: string | null | undefined
): boolean {
  return (
    isOrderStatusConfirmed(orderStatus) ||
    orderStatus === OrderStatus.PRELIMINAR
  );
}

export function getOrderMutationBlockedMessage(
  action: 'editar' | 'eliminar',
  orderStatus?: string | null
): string {
  const status = orderStatus?.trim() || 'desconocido';

  if (action === 'eliminar') {
    switch (status.toUpperCase()) {
      case OrderStatus.EN_PROCESO:
        return 'La OS está en proceso de pago en Financials y no puede eliminarse desde Operations.';
      case OrderStatus.PROCESADO:
        return 'La OS ya fue procesada en Financials y no puede eliminarse.';
      case OrderStatus.ELIMINADO:
        return 'Esta Orden de Servicio ya fue eliminada.';
      default:
        return `Solo se pueden eliminar Órdenes de Servicio en estado CONFIRMADO o PRELIMINAR (estado actual: ${status}).`;
    }
  }

  return `Solo se puede editar una OS confirmada o preliminar (estado actual: ${status}).`;
}
